import { ReactNode } from "react";
import { Trans, useTranslation } from "react-i18next";

import {
  ArrowRight,
  Download,
  PhoneCall,
  Signal,
  Sim,
  Watch,
} from "@/assets/icons/ds";
import { ButtonLink } from "@/components/Interface/Button/Button";
import { Notification } from "@/components/Interface/Notification/Notification";
import { CardModule } from "@/components/Layout/BaseLayouts/CardModule/CardModule";
import { MetricTiles } from "@/components/Layout/MetricTiles/MetricTiles";
import {
  SubscriptionInfoBox,
  SubscriptionInfoGroup,
  SubscriptionInfoRow,
} from "@/components/Layout/SubscriptionInfoBox/SubscriptionInfoBox";
import { portalRoutes } from "@/router/routes/portalRoutes";
import { SimCardDetail } from "@/services/model";
import {
  getFlatNameFromLocalFlatSpeed,
  getProductDescriptionSource,
  hasDifferentSuccessor,
  hasDisplayableProductDescription,
  hasWatchSim,
  isInactive,
  stripFlatName,
} from "@/utils/deviceUtils";
import {
  getCallsVolume,
  getIncludedRoamingVolumeInGb,
  getNextBillDate,
  getSubscriptionSpeeds,
} from "@/utils/tariffUtils";

/**
 * The subscription card: the product the SIM runs on plus what it includes. Flat Mobile
 * subscriptions list their Swiss and international options, Flat Home and
 * data-only subscriptions their speeds.
 */
export const SubscriptionOptions = ({
  simDetail,
  action,
}: {
  simDetail: SimCardDetail;
  action?: ReactNode;
}) => {
  const { t, i18n } = useTranslation("portal", {
    keyPrefix: "cockpit.managementTile.subscription",
  });
  const { t: tDefault } = useTranslation();
  // A SIM whose package has lapsed still knows what it renews into, and the
  // whole card describes that product — so every lookup here goes through the
  // same fallback, not just the ones that happen to have it built in.
  const serviceId = simDetail.serviceID ?? simDetail.successorForRenew?.id;
  const speeds = getSubscriptionSpeeds(simDetail);
  const roamingVolume = getIncludedRoamingVolumeInGb(serviceId);
  // Undefined when the product includes no calls, so the "International"
  // group never ends up as a header with no rows.
  const includedCallsVolume = getCallsVolume({
    t: tDefault,
    productId: serviceId,
  });
  // An inactive SIM without any package has no product to name, but one whose
  // renewal is already planned (e.g. after a failed payment) does.
  const hasProduct =
    !isInactive(simDetail) || hasDisplayableProductDescription(simDetail);
  // Only worth announcing while the SIM still runs on its own package: without
  // `localFlatSpeed` the name above already comes from `successorForRenew`, and
  // the same product would be shown twice. Same-id Watch/Voice drops still
  // count — see `hasDifferentSuccessor`.
  const successor =
    simDetail.localFlatSpeed &&
    simDetail.autoRenew &&
    hasDifferentSuccessor(simDetail)
      ? getFlatNameFromLocalFlatSpeed(simDetail.successorForRenew?.description)
      : undefined;
  const successorDate = getNextBillDate(simDetail, i18n.language);

  return (
    <CardModule
      dataTestid="subscription-options"
      className="self-start"
      title={t("title")}
      actions={action}
      bodyGap={2}
    >
      <SubscriptionInfoBox className="flex items-center gap-3">
        <Sim className="size-6 shrink-0 text-foreground-primary" />
        <p className="text-body-lg font-semibold leading-md text-foreground-primary">
          {hasProduct
            ? // The Watch SIM and the Voice option get their own rows below, so
              // the product name stays bare here.
              stripFlatName(getProductDescriptionSource(simDetail) ?? "")
            : t("noSubscription")}
        </p>
      </SubscriptionInfoBox>

      {speeds && (
        <MetricTiles
          metrics={[
            {
              icon: <Download className="size-6" />,
              value: `${speeds.download}`,
              unit: tDefault("Common.tariff.mbitMax"),
              label: t("options.download"),
            },
            {
              icon: <Download className="size-6 rotate-180" />,
              value: `${speeds.upload}`,
              unit: tDefault("Common.tariff.mbitMax"),
              label: t("options.upload"),
            },
            {
              icon: <Signal className="size-6" />,
              value: speeds.network,
              unit: tDefault("Common.tariff.unlimited"),
            },
          ]}
        />
      )}

      {!speeds && hasProduct && (
        <>
          <SubscriptionInfoGroup label={tDefault("countries.CHE")}>
            <SubscriptionInfoRow
              icon={<Signal className="size-6" />}
              title={tDefault("Common.tariff.dataTelephonySmsCH")}
              detail={tDefault("Common.tariff.unlimited")}
            />
          </SubscriptionInfoGroup>

          {(roamingVolume || includedCallsVolume) && (
            <SubscriptionInfoGroup
              label={tDefault("Common.tariff.international")}
            >
              {roamingVolume && (
                <SubscriptionInfoRow
                  icon={<Signal className="size-6" />}
                  title={t("options.dataRoaming")}
                  detail={tDefault("Common.tariff.volumeGb", {
                    volume: roamingVolume,
                  })}
                />
              )}
              {includedCallsVolume && (
                <SubscriptionInfoRow
                  icon={<PhoneCall className="size-6" />}
                  title={tDefault(
                    "portal:cockpit.managementTile.roaming.showOption.active.callsToAndFromAbroad",
                  )}
                  detail={includedCallsVolume}
                />
              )}
            </SubscriptionInfoGroup>
          )}
        </>
      )}

      {hasWatchSim(simDetail) && (
        <SubscriptionInfoBox className="flex items-center gap-3">
          <div className="flex min-w-0 flex-1 items-center gap-3">
            <Watch className="size-6 shrink-0 text-foreground-primary" />
            <p className="truncate text-body-lg font-semibold leading-md text-foreground-primary">
              {tDefault("Common.products.watchSim")}
            </p>
          </div>
          <ButtonLink
            data-testid="subscription-watch-sim"
            variant="secondary"
            size="sm"
            ariaLabel={tDefault("Common.products.watchSim")}
            icon={<ArrowRight />}
            to={`${portalRoutes.manageDevice.watchSim.base}?simId=${simDetail.simId}`}
            replace
          />
        </SubscriptionInfoBox>
      )}

      {successor && (
        <Notification
          dataTestid="subscription-successor"
          title={
            // The switch happens at the renewal, so the frames name that date
            // — the same one the status card bills on. Without it, only the
            // product can be announced.
            successorDate ? (
              <Trans
                t={t}
                i18nKey="successorOn"
                components={[<span key="0" className="font-semibold" />]}
                values={{ date: successorDate, flatName: successor }}
              />
            ) : (
              t("successor", { flatName: successor })
            )
          }
        />
      )}
    </CardModule>
  );
};
